import type { SendEventParams } from '@/utils/sendEvent';
import { EventData, PixelResponse } from '../../types';
import { BaseTracker } from './BaseTracker';

export interface IAddToCartCustomer {
  email: string;
  phone: string;
  firstName?: string;
  lastName?: string;
}

export interface IAddToCartData {
  itemId: string;
  itemName: string;
  value?: number;
  currency?: string;
  category?: string;
  quantity?: number;
  customer?: IAddToCartCustomer;
  metadata?: Record<string, unknown>;
}

/**
 * Tracker para eventos de adicionar ao carrinho
 */
export class AddToCartTracker extends BaseTracker {
  /**
   * Retorna o payload para envio centralizado no ConversionEngine.
   */
  public getPayload(data: IAddToCartData): SendEventParams {
    this.validateRequiredParams(
      { itemId: data.itemId, itemName: data.itemName },
      ['itemId', 'itemName']
    );
    const {
      itemId,
      itemName,
      value,
      currency = 'BRL',
      category,
      quantity = 1,
      customer,
      metadata,
    } = data;
    return {
      category: 'ECOMMERCE',
      type: 'ADD_TO_CART',
      eventData: {
        itemId,
        itemName,
        value,
        currency,
        category,
        quantity,
        ...(customer ? { customer } : {}),
        ...(metadata ? { metadata } : {}),
      },
      config: this.config,
      sessionId: this.sessionId,
      visitorId: this.visitorId,
      version: this.version,
    };
  }

  /**
   * Rastreia adição de item ao carrinho
   */
  public async track({
    itemId,
    itemName,
    value,
    currency = 'BRL',
    category,
    quantity = 1,
  }: IAddToCartData): Promise<PixelResponse> {
    this.validateRequiredParams({ itemId, itemName }, ['itemId', 'itemName']);
    return this.sendEvent(
      this.getPayload({
        itemId,
        itemName,
        value,
        currency,
        category,
        quantity,
      })
    );
  }

  /**
   * Rastreia adição de múltiplos itens ao carrinho
   */
  public async trackMultipleItems(
    items: Array<{
      itemId: string;
      itemName: string;
      value?: number;
      category?: string;
      quantity?: number;
    }>,
    currency: string = 'BRL'
  ): Promise<PixelResponse> {
    if (!items || items.length === 0) {
      throw new Error('Lista de itens não pode estar vazia');
    }

    const totalValue = items.reduce((sum, item) => sum + (item.value || 0), 0);

    return this.sendEvent({
      category: 'ECOMMERCE',
      type: 'ADD_TO_CART',
      eventData: {
        items,
        total_items: items.length,
        value: totalValue,
        currency,
      },
      config: this.config,
      sessionId: this.sessionId,
      visitorId: this.visitorId,
      version: this.version,
    });
  }

  /**
   * Rastreia adição com parâmetros customizados
   */
  public async trackWithCustomParams(
    addToCartData: IAddToCartData,
    customParams: Record<string, unknown>
  ): Promise<PixelResponse> {
    this.validateRequiredParams(
      { itemId: addToCartData.itemId, itemName: addToCartData.itemName },
      ['itemId', 'itemName']
    );

    return this.sendEvent({
      category: 'ECOMMERCE',
      type: 'ADD_TO_CART',
      eventData: {
        itemId: addToCartData.itemId,
        itemName: addToCartData.itemName,
        value: addToCartData.value,
        currency: addToCartData.currency || 'BRL',
        category: addToCartData.category,
        quantity: addToCartData.quantity || 1,
        ...customParams,
      },
      config: this.config,
      sessionId: this.sessionId,
      visitorId: this.visitorId,
      version: this.version,
    });
  }
}

// Função de conveniência para compatibilidade com código existente
export const addToCart = ({
  itemId,
  itemName,
  value,
  currency,
  category,
  quantity,
}: IAddToCartData): EventData => {
  return {
    event_name: 'ADD_TO_CART',
    event_category: 'ECOMMERCE',
    value,
    currency,
    custom_parameters: {
      item_id: itemId,
      item_name: itemName,
      category,
      quantity,
    },
  };
};
